import { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Search } from 'lucide-react';
import AppShell from './components/ui/AppShell';
import { PageHeader, Button } from './components/ui';
import Pagination from './components/Pagination';
import type { Vehicle } from './types';
import { getVehicles, searchVehicles, createVehicle, updateVehicle, deleteVehicle, resendVerification } from './api';
import { kmToDisplay, distanceUnitLabel } from './utils/units';
import VehicleModal from './components/VehicleModal';
import VehiclePage from './pages/VehiclePage';
import EventModal from './components/EventModal';
import SettingsPage from './pages/SettingsPage';
import BookingsPage from './pages/BookingsPage';
import ExpensesPage from './pages/ExpensesPage';
import RentersPage from './pages/RentersPage';
import CalendarPage from './pages/CalendarPage';
import HubPage from './pages/HubPage';
import BackupPage from './pages/BackupPage';
import DashboardPage from './pages/DashboardPage';
import StatisticsPage from './pages/StatisticsPage';
import ReportsPage from './pages/ReportsPage';
import RecurringPage from './pages/RecurringPage';
import GoMoreAccountPage from './pages/GoMoreAccountPage';
import CancellationsPage from './pages/CancellationsPage';
import GoMoreImportPage from './pages/GoMoreImportPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage';
import { useSettings } from './context/SettingsContext';
import type { Settings } from './context/SettingsContext';
import { useToast } from './context/ToastContext';
import { useAuth } from './context/AuthContext';
import Lightbox from './components/Lightbox';
import './index.css';


function checkDate(d: string, reminderDays: number): 'red' | 'yellow' | 'green' {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const threshold = new Date(today);
  threshold.setDate(threshold.getDate() + reminderDays);
  const date = new Date(d);
  if (date < today) return 'red';
  if (date <= threshold) return 'yellow';
  return 'green';
}

function getDueIndicator(v: Vehicle, reminderDays: number, reminderKm: number): 'red' | 'yellow' | 'green' {
  const results: Array<'red' | 'yellow' | 'green'> = [];

  // Inspection
  if (v.next_inspection) results.push(checkDate(v.next_inspection, reminderDays));
  else results.push('yellow');

  // Service date
  const hasServiceDate = !!v.next_service;
  const hasServiceKm = v.next_service_km != null && v.current_km != null;
  if (hasServiceDate) results.push(checkDate(v.next_service!, reminderDays));
  if (hasServiceKm) {
    const remaining = v.next_service_km! - v.current_km!;
    if (remaining <= 0) results.push('red');
    else if (remaining <= reminderKm) results.push('yellow');
    else results.push('green');
  }
  if (!hasServiceDate && !hasServiceKm) results.push('yellow');

  // Kommende værkstedsbesøg ikke blokeret for udlejning
  if (v.next_unblocked_workshop) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(v.next_unblocked_workshop); d.setHours(0, 0, 0, 0);
    const days = Math.round((d.getTime() - today.getTime()) / 86400000);
    results.push(days <= 7 ? 'red' : 'yellow');
  }

  if (results.includes('red')) return 'red';
  if (results.includes('yellow')) return 'yellow';
  return 'green';
}

const INDICATOR_COLORS = {
  red:    'bg-[var(--color-error)]',
  yellow: 'bg-[var(--color-warning)]',
  green:  'bg-[var(--color-success)]',
};

function formatDate(d: string | undefined | null, fmt: Settings['dateFormat']): string {
  if (!d) return '';
  const date = new Date(d);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  if (fmt === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
  if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
  return `${dd}/${mm}/${yyyy}`;
}

function getDueTooltipLines(v: Vehicle, reminderDays: number, reminderKm: number, dateFmt: Settings['dateFormat'], distUnit: 'km' | 'miles', t: (key: string, opts?: Record<string, unknown>) => string): string[] {
  const distLabel = distanceUnitLabel(distUnit);
  const fmtDist = (km: number) => `${kmToDisplay(km, distUnit)!.toLocaleString('da-DK')} ${distLabel}`;
  const lines: string[] = [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const threshold = new Date(today);
  threshold.setDate(threshold.getDate() + reminderDays);

  // Inspection
  if (v.next_inspection) {
    const d = new Date(v.next_inspection); d.setHours(0, 0, 0, 0);
    if (d < today) lines.push(t('due.inspection_overdue', { date: formatDate(v.next_inspection, dateFmt) }));
    else if (d <= threshold) {
      const days = Math.round((d.getTime() - today.getTime()) / 86400000);
      lines.push(t('due.inspection_soon', { days, date: formatDate(v.next_inspection, dateFmt) }));
    }
  } else {
    lines.push(t('due.inspection_missing'));
  }

  // Service date
  if (v.next_service) {
    const d = new Date(v.next_service); d.setHours(0, 0, 0, 0);
    if (d < today) lines.push(t('due.service_overdue', { date: formatDate(v.next_service, dateFmt) }));
    else if (d <= threshold) {
      const days = Math.round((d.getTime() - today.getTime()) / 86400000);
      lines.push(t('due.service_soon', { days, date: formatDate(v.next_service, dateFmt) }));
    }
  }

  // Service km
  if (v.next_service_km != null && v.current_km != null) {
    const remaining = v.next_service_km - v.current_km;
    if (remaining <= 0) lines.push(t('due.service_km_overdue', { km: fmtDist(v.next_service_km) }));
    else if (remaining <= reminderKm) lines.push(t('due.service_km_soon', { km: fmtDist(remaining), target: fmtDist(v.next_service_km) }));
  }

  if (!v.next_service && !v.next_service_km) {
    lines.push(t('due.service_missing'));
  }

  // Kommende værkstedsbesøg ikke blokeret for udlejning
  if (v.next_unblocked_workshop) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const wd = new Date(v.next_unblocked_workshop); wd.setHours(0, 0, 0, 0);
    const days = Math.round((wd.getTime() - today.getTime()) / 86400000);
    const dateStr = formatDate(v.next_unblocked_workshop, dateFmt);
    if (days <= 0 || days > 7) {
      lines.push(t('due.workshop_unblocked', { date: dateStr }));
    } else {
      lines.push(t('due.workshop_unblocked_soon', { days, date: dateStr }));
    }
  }

  return lines;
}

function VehiclesPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [modalVehicle, setModalVehicle] = useState<Vehicle | null | undefined>(undefined);
  const [eventVehicle, setEventVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<{ id: number; registration_number: string; make?: string; model?: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const limit = settings.itemsPerPage;
  const totalPages = Math.ceil(total / limit);

  const load = useCallback(async () => {
    setLoading(true);
    const params: Record<string, string> = { page: String(page), limit: String(limit) };
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.type = filterType;
    const { data, total: t_ } = await getVehicles(params);
    setVehicles(data);
    setTotal(t_);
    setLoading(false);
  }, [search, filterStatus, filterType, page, limit]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filterStatus, filterType, limit]);

  // Autocomplete
  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      const results = await searchVehicles(val);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    }, 200);
  };

  const pickSuggestion = (reg: string) => {
    setSearch(reg);
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSave = async (v: Vehicle) => {
    try {
      if (v.id) {
        await updateVehicle(v.id, v);
        setModalVehicle(v);
        showSuccess('Ændringer gemt');
        load();
      } else {
        await createVehicle(v);
        setModalVehicle(undefined);
        load();
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Kunne ikke gemme køretøj');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteVehicle(id);
      setModalVehicle(undefined);
      load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Kunne ikke slette køretøj');
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.fleet_group')}
        title={t('nav.vehicles')}
        subtitle={t('vehicles.count_other', { count: total })}
        actions={
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Plus size={14} />}
            onClick={() => setModalVehicle(null)}
          >
            {t('vehicles.add')}
          </Button>
        }
      />

      {/* Filtre */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative" ref={searchRef}>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            type="text"
            placeholder={t('vehicles.search_placeholder')}
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onKeyDown={e => e.key === 'Escape' && setShowSuggestions(false)}
            className="filter-input pl-9 w-64"
          />
          {showSuggestions && (
            <ul className="absolute z-30 top-full mt-1 w-64 bg-cream-50 border border-ink-200 rounded-lg shadow-card-md overflow-hidden">
              {suggestions.map(s => (
                <li key={s.id}
                  onMouseDown={() => pickSuggestion(s.registration_number)}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-sage-50 flex justify-between gap-2">
                  <span className="reg-number">{s.registration_number}</span>
                  <span className="text-ink-500 truncate">{[s.make, s.model].filter(Boolean).join(' ')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <select
          className="filter-input"
          value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">{t('vehicles.active_statuses')}</option>
          <option value="service">{t('status.service')}</option>
          <option value="sold">{t('status.sold')}</option>
          <option value="all">{t('vehicles.all_statuses')}</option>
        </select>

        <select
          className="filter-input"
          value={filterType} onChange={e => setFilterType(e.target.value)}
        >
          <option value="">{t('vehicles.all_types')}</option>
          {(['mini', 'bybil', 'stationcar', 'suv', 'varevogn', 'lastbil'] as const).map(t_ => (
            <option key={t_} value={t_}>{t(`type.${t_}`)}</option>
          ))}
        </select>
      </div>

      {/* Tabel-card */}
      <div className="list-card">
        {loading ? (
          <div className="p-12 text-center text-ink-500">{t('vehicles.loading')}</div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center text-ink-500">{t('vehicles.none_found')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th className="th">{t('vehicles.col_plate')}</th>
                <th className="th">{t('vehicles.col_type')}</th>
                <th className="th">{t('vehicles.col_make_model')}</th>
                <th className="th">{t('vehicles.col_status')}</th>
                <th className="th"></th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(v => {
                const indicator = getDueIndicator(v, settings.inspectionReminderDays, settings.inspectionReminderKm);
                const tooltipLines = indicator
                  ? getDueTooltipLines(v, settings.inspectionReminderDays, settings.inspectionReminderKm, settings.dateFormat, settings.distanceUnit, t)
                  : [];
                return (
                <tr key={v.id} onClick={() => navigate(`/vehicles/${v.id}/overview`)}
                  className="row-hover cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {v.image_url ? (
                        <img src={v.image_url} alt="" onClick={e => { e.stopPropagation(); setLightboxSrc(v.image_url!); }}
                          className="h-[60px] w-[80px] rounded-lg object-cover border border-ink-100 cursor-pointer hover:opacity-80 transition-opacity" />
                      ) : (
                        <div className="h-[60px] w-[80px] rounded-lg border border-ink-100 bg-cream-200 flex items-center justify-center text-ink-300 text-xs">?</div>
                      )}
                      <div>
                        <span className="reg-number">{v.registration_number}</span>
                        {v.current_km != null && (
                          <span className="block text-xs text-ink-500">
                            {kmToDisplay(v.current_km, settings.distanceUnit)!.toLocaleString('da-DK')} {distanceUnitLabel(settings.distanceUnit)}
                            {v.current_km_date && <span className="text-ink-300"> · {formatDate(v.current_km_date, settings.dateFormat)}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-500">{v.vehicle_type ? t(`type.${v.vehicle_type}`) : '—'}</td>
                  <td className="px-4 py-3 text-ink-700">
                    {[v.make, v.model, v.variant].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge badge-${v.status ?? 'active'}`}>
                      {t(`status.${v.status ?? 'active'}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {indicator && (
                        <div className="relative group">
                          <span className={`block w-2.5 h-2.5 rounded-full ${INDICATOR_COLORS[indicator]}`} />
                          {tooltipLines.length > 0 && (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 z-30 hidden group-hover:block
                              bg-ink-900 text-cream-50 text-xs rounded-lg px-3 py-2 shadow-card-md pointer-events-none
                              min-w-max">
                              {tooltipLines.map((line, i) => (
                                <div key={i}>{line}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => setEventVehicle(v)}
                        title={t('events.register')}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-sage-100 text-sage-700 hover:bg-sage-300 hover:text-cream-50 transition-colors">
                        <Plus size={14} strokeWidth={2.5} />
                      </button>
                      <button
                        onClick={() => navigate(`/vehicles/${v.id}/stamdata`)}
                        title={t('vehicles.edit')}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-cream-200 text-ink-700 hover:bg-ink-100 transition-colors">
                        <Pencil size={13} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
            <span>{t('pagination.showing', { from: (page - 1) * limit + 1, to: Math.min(page * limit, total), total })}</span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {modalVehicle !== undefined && (
        <VehicleModal
          vehicle={modalVehicle}
          onSave={handleSave}
          onClose={() => setModalVehicle(undefined)}
          onDelete={modalVehicle?.id ? () => handleDelete(modalVehicle.id!) : undefined}
        />
      )}

      {eventVehicle && (
        <EventModal
          vehicle={eventVehicle}
          onSaved={() => { setEventVehicle(null); }}
          onClose={() => setEventVehicle(null)}
        />
      )}

      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </main>
  );
}

// /vehicles/:id/events → historik-fanen. Ruten holdes i live for gamle links og bogmærker.
//
// Stod tidligere som <Navigate to="../history">, men i React Router tæller ".." et RUTE-niveau,
// ikke et URL-segment. Ruten ligger fladt uden forælder, så ".." blev til roden og målet endte
// som /history — en adresse der ikke findes. Uden en catch-all rendrede <Routes> ingenting, og
// "Åbn begivenhed" på en værksted-udgift gav derfor en blank side.
//
// Absolut sti frem for relativ, og query-strengen føres med: den bærer ?type= og ?id=, som
// historikken bruger til at åbne den rigtige begivenhed.
function EventsTabRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/vehicles/${id}/history${search}`} replace />;
}

// Uden denne rute rendrer <Routes> ingenting på en ukendt adresse, og man får en blank side
// uden en anelse om hvad der gik galt. Det kostede en fejlsøgning at finde ud af at et
// redirect pegede på /history i stedet for /vehicles/:id/history.
function NotFound() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <main className="max-w-2xl mx-auto px-6 py-20 text-center">
      <h1 className="page-title mb-2">Siden findes ikke</h1>
      <p className="text-ink-500 mb-1">Der er ingen side på denne adresse:</p>
      <p className="font-mono text-sm text-ink-700 bg-cream-100 rounded-lg px-3 py-2 inline-block mb-6 break-all">{pathname}</p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={() => navigate(-1)} className="btn btn-secondary">Tilbage</button>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary">Til overblik</button>
      </div>
    </main>
  );
}

export default function App() {
  const { t } = useTranslation();
  const { user, logout, loading: authLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (window.location.pathname.startsWith('/hub')) {
      document.title = window.location.pathname === '/hub/backup' ? 'Backup - HUB' : 'HUB - RentalFleetOS';
      return;
    }
    const PAGE_TITLE_KEYS: Record<string, string> = {
      '/vehicles':   'nav.vehicles',
      '/dashboard':  'nav.dashboard',
      '/bookings':   'nav.bookings',
      '/expenses':   'nav.expenses',
      '/recurring':  'nav.recurring',
      '/statistics': 'nav.statistics',
      '/calendar':   'nav.calendar',
      '/settings':   'nav.settings',
    };
    const key = PAGE_TITLE_KEYS[location.pathname];
    document.title = key ? `${t(key)} - RentalFleetOS` : 'RentalFleetOS';
  }, [location.pathname, t]);

  if (authLoading) return null;

  // Hub — separat layout, kræver superadmin
  if (window.location.pathname.startsWith('/hub')) {
    if (!user) return <LoginPage />;
    if (!user.isSuperadmin) return <div className="p-8 text-[var(--color-error)]">Kun superadmin har adgang til hub.</div>;
    if (window.location.pathname === '/hub/backup') return <BackupPage />;
    return <HubPage />;
  }

  // Public auth-routes (altid tilgængelige uden login)
  if (location.pathname === '/verify-email') return <VerifyEmailPage />;
  if (location.pathname === '/confirm-email-change') return <ConfirmEmailChangePage />;
  if (location.pathname === '/signup') return user ? <Navigate to="/dashboard" replace /> : <SignupPage />;

  if (!user) return <LoginPage />;

  // Logged ind men email ikke bekræftet
  if (!user.isSuperadmin && user.emailVerified === false) {
    return <UnverifiedScreen email={user.email} onLogout={logout} />;
  }

  // Logged ind, verificeret, men kunde-status er ikke aktiv (pending_review, rejected osv.)
  if (!user.isSuperadmin && user.customerStatus && user.customerStatus !== 'active') {
    return <CustomerNotActiveScreen status={user.customerStatus} onLogout={logout} />;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        {/* Udløbet session sender til /login; efter login står URL'en der stadig */}
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/vehicles" element={<VehiclesPage />} />
        <Route path="/vehicles/:id" element={<VehiclePage />} />
        <Route path="/vehicles/:id/:tab" element={<VehiclePage />} />
        <Route path="/vehicles/:id/events" element={<EventsTabRedirect />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/fleet" element={<Navigate to="/bookings" replace />} />
        <Route path="/recurring" element={<RecurringPage />} />
        <Route path="/renters" element={<RentersPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/gomore" element={<GoMoreAccountPage />} />
        <Route path="/cancellations" element={<CancellationsPage />} />
        <Route path="/gomore/import" element={<GoMoreImportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

function UnverifiedScreen({ email, onLogout }: { email: string; onLogout: () => void }) {
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await resendVerification();
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke sende mail');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border p-8 text-center">
        <div className="mx-auto w-12 h-12 bg-[var(--color-primary-soft-bg)] rounded-full flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Bekræft din email</h1>
        <p className="text-sm text-gray-600 mb-1">Vi har sendt et link til</p>
        <p className="text-sm font-mono text-gray-800 mb-4">{email}</p>
        <p className="text-sm text-gray-500 mb-6">Klik på linket i mailen for at aktivere din konto.</p>
        {sent && <p className="text-sm text-[var(--color-success)] mb-4">Ny mail er sendt</p>}
        {error && <p className="text-sm text-[var(--color-error)] mb-4">{error}</p>}
        <button onClick={handleResend} disabled={resending || sent}
          className="text-sm text-[var(--color-primary)] hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
          {resending ? 'Sender…' : sent ? 'Mail sendt' : 'Send mailen igen'}
        </button>
        <div className="mt-6 pt-6 border-t border-gray-100">
          <button onClick={onLogout} className="text-xs text-gray-400 hover:text-gray-600">Log ud</button>
        </div>
      </div>
    </div>
  );
}

function CustomerNotActiveScreen({ status, onLogout }: { status: string; onLogout: () => void }) {
  const messages: Record<string, { title: string; body: string }> = {
    pending_review: {
      title: 'Din konto afventer godkendelse',
      body: 'Vi gennemgår din ansøgning manuelt fordi der allerede er flere udlejere i dit område. Du får besked på mail når den er godkendt.',
    },
    rejected: {
      title: 'Din ansøgning er afvist',
      body: 'Kontakt os hvis du mener dette er en fejl.',
    },
    suspended: {
      title: 'Din konto er suspenderet',
      body: 'Kontakt os for at få den genaktiveret.',
    },
    cancelled: {
      title: 'Din konto er lukket',
      body: 'Kontakt os hvis du vil genåbne den.',
    },
  };
  const msg = messages[status] ?? { title: 'Din konto er ikke aktiv', body: `Status: ${status}` };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border p-8 text-center">
        <div className="mx-auto w-12 h-12 bg-[var(--color-warning-100)] rounded-full flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-[var(--color-warning)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{msg.title}</h1>
        <p className="text-sm text-gray-600 mb-6">{msg.body}</p>
        <button onClick={onLogout} className="text-sm text-[var(--color-primary)] hover:underline">Log ud</button>
      </div>
    </div>
  );
}
